import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { crmImportAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Upload, CheckCircle2, AlertTriangle, Users, Check,
  Briefcase, FileSpreadsheet, Loader2, RotateCcw, ExternalLink, Shield,
  Calculator, Receipt, SkipForward, BookOpen, Info,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const CRM_OPTIONS = [
  { value: 'hcp', label: 'Housecall Pro', jobLabel: 'Jobs', logo: '/crm-logos/hcp.png' },
  { value: 'servicetitan', label: 'ServiceTitan', jobLabel: 'Jobs', logo: '/crm-logos/servicetitan.png' },
  { value: 'jobber', label: 'Jobber', jobLabel: 'Jobs', logo: '/crm-logos/jobber.png' },
  { value: 'markate', label: 'Markate', jobLabel: 'Work Orders', logo: '/crm-logos/markate.png' },
  { value: 'generic', label: 'Other CSV', jobLabel: 'Jobs', logo: null },
];

const ALL_STEPS = [
  { key: 'crm', title: 'Choose Source' },
  { key: 'customers_upload', title: 'Upload Customers' },
  { key: 'customers_map', title: 'Map Customer Fields' },
  { key: 'customers_preview', title: 'Customer Preview' },
  { key: 'jobs_upload', title: 'Upload Jobs' },
  { key: 'jobs_map', title: 'Map Job Fields' },
  { key: 'jobs_preview', title: 'Job Preview' },
  { key: 'estimates_upload', title: 'Upload Estimates' },
  { key: 'estimates_map', title: 'Map Estimate Fields' },
  { key: 'estimates_preview', title: 'Estimate Preview' },
  { key: 'invoices_upload', title: 'Upload Invoices' },
  { key: 'invoices_map', title: 'Map Invoice Fields' },
  { key: 'invoices_preview', title: 'Invoice Preview' },
  { key: 'summary', title: 'Migration Summary' },
  { key: 'execute', title: 'Import' },
  { key: 'complete', title: 'Complete' },
];

// Step indices
const STEP = {
  CRM: 0,
  CUST_UPLOAD: 1, CUST_MAP: 2, CUST_PREVIEW: 3,
  JOB_UPLOAD: 4, JOB_MAP: 5, JOB_PREVIEW: 6,
  EST_UPLOAD: 7, EST_MAP: 8, EST_PREVIEW: 9,
  INV_UPLOAD: 10, INV_MAP: 11, INV_PREVIEW: 12,
  SUMMARY: 13, EXECUTE: 14, COMPLETE: 15,
};

const CUSTOMER_FIELDS = [
  { value: '_skip', label: '-- Skip --' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'display_name', label: 'Display Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'secondary_phone', label: 'Alt Phone' },
  { value: 'street', label: 'Street' },
  { value: 'service_unit', label: 'Unit / Suite' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'Zip' },
  { value: 'company', label: 'Company' },
  { value: 'notes', label: 'Notes' },
  { value: 'source_id', label: 'Source ID' },
  { value: 'customer_type', label: 'Type' },
  { value: 'tags', label: 'Tags' },
  { value: 'lead_source', label: 'Lead Source' },
];

const JOB_FIELDS = [
  { value: '_skip', label: '-- Skip --' },
  { value: 'job_number', label: 'Job Number' },
  { value: 'job_name', label: 'Job Name' },
  { value: 'customer_name', label: 'Customer Name' },
  { value: '_customer_email', label: 'Customer Email (matching)' },
  { value: '_customer_phone', label: 'Customer Phone (matching)' },
  { value: '_customer_source_id', label: 'Customer ID (matching)' },
  { value: 'street', label: 'Street' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'Zip' },
  { value: 'job_type', label: 'Job Type' },
  { value: 'status', label: 'Status' },
  { value: 'scheduled_date', label: 'Scheduled Date' },
  { value: 'technician', label: 'Technician' },
  { value: 'description', label: 'Description' },
  { value: 'amount', label: 'Amount' },
  { value: 'tags', label: 'Tags' },
  { value: 'priority', label: 'Priority' },
  { value: 'completed_at', label: 'Completed Date' },
  { value: 'source_id', label: 'Source ID' },
  { value: '_location', label: 'Location (helper)' },
];

const ESTIMATE_FIELDS = [
  { value: '_skip', label: '-- Skip --' },
  { value: 'estimate_number', label: 'Estimate Number' },
  { value: 'estimate_name', label: 'Estimate Name' },
  { value: 'description', label: 'Description / Summary' },
  { value: 'amount', label: 'Amount / Subtotal' },
  { value: 'tax', label: 'Tax' },
  { value: 'status', label: 'Status' },
  { value: 'notes', label: 'Notes' },
  { value: 'approved_at', label: 'Approved / Sold Date' },
  { value: 'customer_name', label: 'Customer Name' },
  { value: '_customer_email', label: 'Customer Email (matching)' },
  { value: '_customer_phone', label: 'Customer Phone (matching)' },
  { value: '_customer_source_id', label: 'Customer ID (matching)' },
  { value: 'job_number', label: 'Parent Job Number' },
  { value: '_job_source_id', label: 'Job ID (matching)' },
  { value: 'street', label: 'Street' },
  { value: 'service_unit', label: 'Unit / Suite' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'Zip' },
  { value: 'tags', label: 'Tags' },
  { value: 'source_id', label: 'Source ID' },
];

const INVOICE_FIELDS = [
  { value: '_skip', label: '-- Skip --' },
  { value: 'invoice_number', label: 'Invoice Number' },
  { value: 'description', label: 'Description / Summary' },
  { value: 'amount', label: 'Subtotal' },
  { value: 'tax', label: 'Tax' },
  { value: 'amount_total', label: 'Total' },
  { value: 'status', label: 'Status' },
  { value: 'notes', label: 'Notes' },
  { value: 'issued_at', label: 'Invoice Date' },
  { value: 'paid_at', label: 'Paid Date' },
  { value: 'customer_name', label: 'Customer Name' },
  { value: '_customer_email', label: 'Customer Email (matching)' },
  { value: '_customer_phone', label: 'Customer Phone (matching)' },
  { value: '_customer_source_id', label: 'Customer ID (matching)' },
  { value: 'job_number', label: 'Job Number' },
  { value: '_job_source_id', label: 'Job ID (matching)' },
  { value: 'street', label: 'Street' },
  { value: 'service_unit', label: 'Unit / Suite' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'Zip' },
  { value: 'source_id', label: 'Source ID' },
];

const FIELD_OPTIONS_MAP = {
  customers: CUSTOMER_FIELDS,
  jobs: JOB_FIELDS,
  estimates: ESTIMATE_FIELDS,
  invoices: INVOICE_FIELDS,
};

const ENTITY_CONFIG = {
  customers: { icon: Users, label: 'Customers', color: 'text-blue-600' },
  jobs: { icon: Briefcase, label: 'Jobs', color: 'text-emerald-600' },
  estimates: { icon: Calculator, label: 'Estimates', color: 'text-purple-600' },
  invoices: { icon: Receipt, label: 'Invoices', color: 'text-orange-600' },
};

// Per-batch time estimates (seconds) for rough ETA calculation
const BATCH_TIME_SEC = { customers: 36, jobs: 28, estimates: 25, invoices: 29 };
const BATCH_SIZE = 100;

/** Scale preview sample counts to full-file estimates */
function estimateFullFileCounts(preview, totalRows) {
  if (!preview) return { newCount: 0, dupCount: 0, reviewCount: 0 };
  const sampled = preview.preview_row_count || 0;
  if (sampled <= 0) return { newCount: 0, dupCount: 0, reviewCount: 0 };
  if (sampled >= totalRows) {
    return { newCount: preview.new_count || 0, dupCount: preview.duplicate_count || 0, reviewCount: preview.review_count || 0 };
  }
  const ratio = totalRows / sampled;
  const dupCount = Math.round((preview.duplicate_count || 0) * ratio);
  const reviewCount = preview.review_count || 0; // review only from sample
  const newCount = Math.max(0, totalRows - dupCount - reviewCount);
  return { newCount, dupCount, reviewCount };
}

// ---------------------------------------------------------------------------
// Phase grouping for the 3-step progress indicator
// ---------------------------------------------------------------------------
const PHASE = { UPLOAD: 0, REVIEW: 1, IMPORT: 2 };
const PHASES = [
  { label: 'Upload', icon: Upload },
  { label: 'Review', icon: FileSpreadsheet },
  { label: 'Import', icon: CheckCircle2 },
];
const REVIEW_STEPS = new Set([STEP.CUST_PREVIEW, STEP.JOB_PREVIEW, STEP.EST_PREVIEW, STEP.INV_PREVIEW, STEP.SUMMARY]);
function getPhase(step) {
  if (step >= STEP.EXECUTE) return PHASE.IMPORT;
  if (REVIEW_STEPS.has(step)) return PHASE.REVIEW;
  return PHASE.UPLOAD;
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------
function PhaseIndicator({ step, subtitle }) {
  const phase = getPhase(step);
  return (
    <div className="mb-6" data-testid="wizard-progress">
      <div className="flex items-center gap-1 mb-2">
        {PHASES.map((p, i) => {
          const Icon = p.icon;
          const done = i < phase;
          const active = i === phase;
          return (
            <React.Fragment key={p.label}>
              {i > 0 && <div className={`flex-1 h-0.5 mx-1 rounded ${done || active ? 'bg-primary' : 'bg-muted-foreground/20'}`} />}
              <div className="flex items-center gap-1.5">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors
                  ${done ? 'bg-primary text-primary-foreground' : active ? 'bg-primary text-primary-foreground ring-2 ring-primary/30 ring-offset-1' : 'bg-muted text-muted-foreground'}`}
                  data-testid={`phase-${p.label.toLowerCase()}`}
                >
                  {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                </div>
                <span className={`text-xs font-medium whitespace-nowrap ${active ? 'text-foreground' : done ? 'text-muted-foreground' : 'text-muted-foreground/60'}`}>
                  {p.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {subtitle && <p className="text-xs text-muted-foreground" data-testid="phase-subtitle">{subtitle}</p>}
    </div>
  );
}

function MappingSummary({ headers, mapping, fieldOptions }) {
  const willImport = headers.filter(h => mapping[h] && mapping[h] !== '_skip');
  const willSkip = headers.filter(h => !mapping[h] || mapping[h] === '_skip');
  const getLabel = (h) => fieldOptions.find(fo => fo.value === mapping[h])?.label || mapping[h];
  return (
    <div className="grid grid-cols-2 gap-3 mt-3" data-testid="mapping-summary">
      <div className="border rounded-lg p-3 bg-green-50/50">
        <p className="text-xs font-semibold text-green-700 mb-1.5" data-testid="mapped-count">
          Will Import: {willImport.length} field{willImport.length !== 1 ? 's' : ''}
        </p>
        <div className="flex flex-wrap gap-1">
          {willImport.map(h => (
            <Badge key={h} variant="outline" className="text-[10px] bg-green-100 text-green-800 border-green-200">
              {h} &rarr; {getLabel(h)}
            </Badge>
          ))}
        </div>
      </div>
      <div className="border rounded-lg p-3 bg-gray-50">
        <p className="text-xs font-semibold text-gray-500 mb-1.5" data-testid="unmapped-count">
          Will Skip: {willSkip.length} field{willSkip.length !== 1 ? 's' : ''}
        </p>
        <div className="flex flex-wrap gap-1">
          {willSkip.map(h => (
            <Badge key={h} variant="outline" className="text-[10px] bg-gray-100 text-gray-500 border-gray-200">
              {h}
            </Badge>
          ))}
          {willSkip.length === 0 && <span className="text-xs text-gray-400 italic">All fields mapped</span>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function MigrationWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [crm, setCrm] = useState('');

  // Entity state is stored generically per entity type
  const [entities, setEntities] = useState({
    customers: { session: null, headers: [], mapping: {}, preview: null, review: [], summary: null, file: null, totalRows: 0, skipped: false },
    jobs:      { session: null, headers: [], mapping: {}, preview: null, review: [], summary: null, file: null, totalRows: 0, skipped: false },
    estimates: { session: null, headers: [], mapping: {}, preview: null, review: [], summary: null, file: null, totalRows: 0, skipped: false },
    invoices:  { session: null, headers: [], mapping: {}, preview: null, review: [], summary: null, file: null, totalRows: 0, skipped: false },
  });

  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executionPhase, setExecutionPhase] = useState('');
  const [templates, setTemplates] = useState({});
  const [assignableCount, setAssignableCount] = useState(null);
  const [assignableChecked, setAssignableChecked] = useState(false);

  const crmOption = CRM_OPTIONS.find(c => c.value === crm);
  const jobLabel = crmOption?.jobLabel || 'Jobs';

  // Helper to update entity state
  const updateEntity = useCallback((entityType, updates) => {
    setEntities(prev => ({
      ...prev,
      [entityType]: { ...prev[entityType], ...updates },
    }));
  }, []);

  // Build visible steps based on what's been skipped
  const visibleSteps = useMemo(() => {
    const steps = [...ALL_STEPS];
    // Replace job labels with CRM-specific
    return steps.map(s => {
      if (s.key === 'jobs_upload') return { ...s, title: `Upload ${jobLabel}` };
      if (s.key === 'jobs_map') return { ...s, title: `Map ${jobLabel} Fields` };
      if (s.key === 'jobs_preview') return { ...s, title: `${jobLabel} Preview` };
      return s;
    });
  }, [jobLabel]);

  // Load templates when CRM is selected
  useEffect(() => {
    if (!crm) return;
    const load = async () => {
      try {
        const [cRes, jRes, eRes, iRes] = await Promise.all([
          crmImportAPI.listTemplates('customers'),
          crmImportAPI.listTemplates('jobs'),
          crmImportAPI.listTemplates('estimates').catch(() => ({ data: { templates: [] } })),
          crmImportAPI.listTemplates('invoices').catch(() => ({ data: { templates: [] } })),
        ]);
        setTemplates({
          customers: cRes.data.templates || [],
          jobs: jRes.data.templates || [],
          estimates: eRes.data.templates || [],
          invoices: iRes.data.templates || [],
        });
      } catch { /* ignore */ }
    };
    load();
  }, [crm]);

  // Check assignable users when entering job upload step
  useEffect(() => {
    if (step === STEP.JOB_UPLOAD && !assignableChecked) {
      crmImportAPI.assignableUsersCount()
        .then(res => {
          setAssignableCount(res.data.assignable_count ?? 0);
          setAssignableChecked(true);
        })
        .catch(() => {
          setAssignableCount(null);
          setAssignableChecked(true);
        });
    }
  }, [step, assignableChecked]);

  // ---------------------------------------------------------------------------
  // Upload handler (generic for all entity types)
  // ---------------------------------------------------------------------------
  const handleUpload = useCallback(async (file, entityType) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);
      formData.append('source_system', crm);
      const res = await crmImportAPI.upload(formData);
      const data = res.data;

      // Find best matching template
      const entityTemplates = templates[entityType] || [];
      const crmPreset = entityTemplates.find(t => t.crm_name === (data.detected_crm || crm) && !t.tenant_id);
      const mapping = crmPreset ? crmPreset.mapping_json : (data.suggested_mapping || {});

      const mapStep = { customers: STEP.CUST_MAP, jobs: STEP.JOB_MAP, estimates: STEP.EST_MAP, invoices: STEP.INV_MAP };
      updateEntity(entityType, {
        session: data.session_id,
        headers: data.headers,
        mapping,
        totalRows: data.total_rows,
        file,
        skipped: false,
      });
      setStep(mapStep[entityType]);
      toast.success(`${data.total_rows} rows detected${data.detected_crm ? ` (${data.detected_crm} format)` : ''}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  }, [crm, templates, updateEntity]);

  // ---------------------------------------------------------------------------
  // Preview handler
  // ---------------------------------------------------------------------------
  const handlePreview = useCallback(async (entityType) => {
    const ent = entities[entityType];
    setLoading(true);
    try {
      await crmImportAPI.confirmMapping(ent.session, ent.mapping);
      const prevRes = await crmImportAPI.preview(ent.session);
      const preview = prevRes.data;

      let review = [];
      if (preview.review_count > 0) {
        const revRes = await crmImportAPI.getReviewRows(ent.session);
        review = revRes.data.rows || [];
      }

      const previewStep = { customers: STEP.CUST_PREVIEW, jobs: STEP.JOB_PREVIEW, estimates: STEP.EST_PREVIEW, invoices: STEP.INV_PREVIEW };
      updateEntity(entityType, { preview, review });
      setStep(previewStep[entityType]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  }, [entities, updateEntity]);

  // ---------------------------------------------------------------------------
  // Resolve review row
  // ---------------------------------------------------------------------------
  const handleResolve = useCallback(async (entityType, rowId, action, matchId) => {
    const ent = entities[entityType];
    try {
      await crmImportAPI.resolveRow(ent.session, rowId, action, matchId);
      updateEntity(entityType, {
        review: ent.review.filter(r => r.id !== rowId),
      });
      toast.success('Resolved');
    } catch {
      toast.error('Failed to resolve');
    }
  }, [entities, updateEntity]);

  // ---------------------------------------------------------------------------
  // Skip entity
  // ---------------------------------------------------------------------------
  const handleSkip = useCallback((entityType) => {
    updateEntity(entityType, { skipped: true });
    // Advance to next section
    const nextUpload = {
      jobs: STEP.EST_UPLOAD,
      estimates: STEP.INV_UPLOAD,
      invoices: STEP.SUMMARY,
    };
    setStep(nextUpload[entityType]);
  }, [updateEntity]);

  // ---------------------------------------------------------------------------
  // Next step after preview
  // ---------------------------------------------------------------------------
  const getNextAfterPreview = (entityType) => {
    const nextMap = {
      customers: STEP.JOB_UPLOAD,
      jobs: STEP.EST_UPLOAD,
      estimates: STEP.INV_UPLOAD,
      invoices: STEP.SUMMARY,
    };
    return nextMap[entityType];
  };

  const getNextLabel = (entityType) => {
    const labels = {
      customers: `Upload ${jobLabel}`,
      jobs: 'Continue',
      estimates: 'Continue',
      invoices: 'Review Summary',
    };
    return labels[entityType];
  };

  // ---------------------------------------------------------------------------
  // Execute migration (background job + polling)
  // ---------------------------------------------------------------------------
  const [executeProgress, setExecuteProgress] = useState({});
  const [executeFailed, setExecuteFailed] = useState(null);

  const pollExecuteStatus = useCallback(async (entityType, sessionId) => {
    const MAX_POLLS = 600; // 10 min at 1s interval
    let authFailCount = 0;
    const MAX_AUTH_FAILS = 3;
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, i < 10 ? 1000 : 2000));
      try {
        const res = await crmImportAPI.executeStatus(sessionId);
        const d = res.data;
        authFailCount = 0; // reset on success
        setExecuteProgress(prev => ({ ...prev, [entityType]: d }));

        if (d.status === 'completed') return d;
        if (d.status === 'failed') {
          throw new Error(d.error_message || 'Import failed');
        }
      } catch (err) {
        // Handle 401 / token expired — stop polling gracefully instead of spamming
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          authFailCount++;
          if (authFailCount >= MAX_AUTH_FAILS) {
            // Stop polling; import continues server-side
            setExecuteProgress(prev => ({
              ...prev,
              [entityType]: {
                ...prev[entityType],
                status: 'completed',
                progress_message: 'Import is running server-side. Check Migration History for results.',
                imported_count: prev[entityType]?.progress_current || 0,
              }
            }));
            return { status: 'completed' };
          }
          continue; // retry a couple times in case of transient auth issue
        }
        if (err.message && err.message !== 'Import failed') continue;
        throw err;
      }
    }
    throw new Error('Import timed out — check the Migration History for results');
  }, []);

  const handleExecute = useCallback(async () => {
    setExecuting(true);
    setExecuteFailed(null);
    setExecuteProgress({});
    const order = ['customers', 'jobs', 'estimates', 'invoices'];
    const newEntities = { ...entities };
    try {
      for (const entityType of order) {
        const ent = entities[entityType];
        if (!ent.session || ent.skipped) continue;
        const cfg = ENTITY_CONFIG[entityType];
        setExecutionPhase(`Importing ${cfg.label.toLowerCase()}...`);
        setExecuteProgress(prev => ({
          ...prev,
          [entityType]: { status: 'queued', progress_current: 0, progress_total: ent.totalRows || 0, progress_message: 'Queued...' }
        }));

        // Enqueue the background job
        const execRes = await crmImportAPI.execute(ent.session);
        const { job_id } = execRes.data;
        setExecuteProgress(prev => ({
          ...prev,
          [entityType]: { status: 'pending', progress_current: 0, progress_total: ent.totalRows || 0, progress_message: 'Starting...', job_id }
        }));

        // Poll until complete
        await pollExecuteStatus(entityType, ent.session);

        // Fetch final summary
        const sumRes = await crmImportAPI.getSummary(ent.session);
        newEntities[entityType] = { ...newEntities[entityType], summary: sumRes.data };
      }
      setEntities(newEntities);
      setExecutionPhase('');
      setStep(STEP.COMPLETE);
      toast.success('Migration complete!');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Migration failed';
      toast.error(msg);
      setExecuteFailed(msg);
      setExecutionPhase('');
      // Mark remaining entities as blocked
      setExecuteProgress(prev => {
        const updated = { ...prev };
        for (const et of order) {
          const ent = entities[et];
          if (!ent.session || ent.skipped) continue;
          if (!updated[et] || (updated[et].status !== 'completed' && updated[et].status !== 'failed')) {
            updated[et] = { ...updated[et], status: 'blocked', progress_message: 'Blocked — previous import failed' };
          }
        }
        return updated;
      });
    } finally {
      setExecuting(false);
    }
  }, [entities, pollExecuteStatus]);

  // ---------------------------------------------------------------------------
  // Rollback
  // ---------------------------------------------------------------------------
  const handleRollback = useCallback(async () => {
    if (!window.confirm('This will undo the entire migration. Are you sure?')) return;
    setLoading(true);
    try {
      // Rollback in reverse order
      const order = ['invoices', 'estimates', 'jobs', 'customers'];
      for (const entityType of order) {
        if (entities[entityType].session && !entities[entityType].skipped) {
          await crmImportAPI.rollback(entities[entityType].session);
        }
      }
      toast.success('Migration rolled back');
      setStep(0);
      setCrm('');
      setEntities({
        customers: { session: null, headers: [], mapping: {}, preview: null, review: [], summary: null, file: null, totalRows: 0, skipped: false },
        jobs:      { session: null, headers: [], mapping: {}, preview: null, review: [], summary: null, file: null, totalRows: 0, skipped: false },
        estimates: { session: null, headers: [], mapping: {}, preview: null, review: [], summary: null, file: null, totalRows: 0, skipped: false },
        invoices:  { session: null, headers: [], mapping: {}, preview: null, review: [], summary: null, file: null, totalRows: 0, skipped: false },
      });
    } catch {
      toast.error('Rollback failed');
    } finally {
      setLoading(false);
    }
  }, [entities]);

  // ---------------------------------------------------------------------------
  // Render: Choose CRM
  // ---------------------------------------------------------------------------
  const renderStepCRM = () => (
    <div className="space-y-6" data-testid="step-choose-crm">
      <div>
        <h2 className="text-2xl font-bold mb-2">Where are you migrating from?</h2>
        <p className="text-muted-foreground">Select your current CRM to auto-configure templates and field mappings.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl">
        {CRM_OPTIONS.map(opt => (
          <button
            key={opt.value}
            data-testid={`crm-option-${opt.value}`}
            className={`p-4 rounded-xl border-2 text-center transition-all hover:shadow-md ${
              crm === opt.value ? 'border-primary bg-primary/5 shadow-md' : 'border-border hover:border-primary/50'
            }`}
            onClick={() => setCrm(opt.value)}
          >
            {opt.logo ? (
              <div className="h-10 flex items-center justify-center mb-1.5">
                <img src={opt.logo} alt={opt.label} className="max-h-10 max-w-[80px] object-contain" />
              </div>
            ) : (
              <div className="h-10 flex items-center justify-center mb-1.5">
                <FileSpreadsheet className="w-7 h-7 text-muted-foreground" />
              </div>
            )}
            <span className="text-sm font-medium">{opt.label}</span>
          </button>
        ))}
      </div>
      <p className="text-sm text-muted-foreground" data-testid="wizard-helper-text">
        You'll upload Customers first, then {jobLabel}, and optionally Estimates and Invoices.<br />
        This usually takes less than 5 minutes.
      </p>
      {crm && (
        <div>
          <Button onClick={() => setStep(STEP.CUST_UPLOAD)} data-testid="btn-next-to-upload" size="lg">
            Continue with {crmOption?.label} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render: Upload step (generic)
  // ---------------------------------------------------------------------------
  const renderUploadStep = (entityType) => {
    const cfg = ENTITY_CONFIG[entityType];
    const Icon = cfg.icon;
    const label = entityType === 'jobs' ? jobLabel : cfg.label;
    const isOptional = entityType !== 'customers';
    const backStepMap = {
      customers: STEP.CRM,
      jobs: STEP.CUST_PREVIEW,
      estimates: STEP.JOB_PREVIEW,
      invoices: STEP.EST_PREVIEW,
    };
    // If previous entity was skipped, go back further
    const getBackStep = () => {
      if (entityType === 'estimates' && entities.jobs.skipped) return STEP.CUST_PREVIEW;
      if (entityType === 'invoices' && entities.estimates.skipped) {
        if (entities.jobs.skipped) return STEP.CUST_PREVIEW;
        return STEP.JOB_PREVIEW;
      }
      return backStepMap[entityType];
    };

    const descriptions = {
      customers: `Export your Customers from ${crmOption?.label || 'your CRM'} as a CSV file and upload it here.`,
      jobs: `Upload your ${label} export. We'll link them to the customers you just uploaded.`,
      estimates: `Optionally upload Estimates. We'll link them to imported customers and jobs.`,
      invoices: `Optionally upload Invoices. We'll link them to imported customers and jobs.`,
    };

    return (
      <div className="space-y-6 max-w-xl" data-testid={`step-upload-${entityType}`}>
        <div>
          <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-3`}>
            <Icon className="w-7 h-7 text-primary" />
          </div>
          <h2 className="text-xl font-bold mb-1">Upload {label} Export</h2>
          <p className="text-muted-foreground text-sm">{descriptions[entityType]}</p>
          {isOptional && (
            <Badge variant="secondary" className="mt-2">Optional</Badge>
          )}
        </div>

        {/* Assignable user check for jobs */}
        {entityType === 'jobs' && assignableChecked && assignableCount === 0 && (
          <Card className="border-red-200 bg-red-50" data-testid="jobs-blocker-no-assignable">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-900">No Assignable Team Members</p>
                <p className="text-xs text-red-700 mt-1">
                  Before importing jobs, you need at least one assignable team member so imported jobs can be assigned correctly.
                </p>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate('/team')} data-testid="btn-go-to-team">
                  <Users className="w-3.5 h-3.5 mr-1" /> Add Team Members
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        {entityType === 'jobs' && assignableChecked && assignableCount === 1 && (
          <Card className="border-amber-200 bg-amber-50" data-testid="jobs-warning-one-assignable">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-900">Limited Assignable Team Members</p>
                <p className="text-xs text-amber-700 mt-1">
                  Your account currently has 1 assignable team member. If your imported jobs contain assignments for additional technicians, those jobs may import as unassigned.
                </p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => navigate('/team')} data-testid="btn-add-team-members">
                    <Users className="w-3.5 h-3.5 mr-1" /> Add Team Members
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Block upload when no assignable users for jobs */}
        {!(entityType === 'jobs' && assignableCount === 0) && (
        <label
          className="block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/20 transition-colors"
          data-testid={`dropzone-${entityType}`}
        >
          <input
            type="file"
            accept=".csv,.xlsx"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.[0]) handleUpload(e.target.files[0], entityType);
            }}
          />
          {loading ? (
            <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-primary" />
          ) : (
            <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          )}
          <p className="font-medium">{loading ? 'Processing...' : 'Click to upload CSV file'}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {crmOption?.value !== 'generic'
              ? `${crmOption?.label} ${label} export format auto-detected`
              : 'Generic CSV — you\'ll map fields manually'}
          </p>
        </label>
        )}
        {entityType !== 'customers' && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg">
            <Shield className="w-4 h-4 flex-shrink-0" />
            <span>{label} will be linked to imported {entityType === 'jobs' ? 'customers' : 'customers and jobs'} using {crm === 'servicetitan' ? 'source IDs' : 'name, email, and phone matching'}.</span>
          </div>
        )}
        <div className="flex justify-between">
          <Button variant="ghost" onClick={() => setStep(getBackStep())} data-testid="btn-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {isOptional && (
            <Button variant="outline" onClick={() => handleSkip(entityType)} data-testid={`btn-skip-${entityType}`}>
              <SkipForward className="w-4 h-4 mr-1" /> Skip {label}
            </Button>
          )}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Mapping step (generic)
  // ---------------------------------------------------------------------------
  const renderMappingStep = (entityType) => {
    const ent = entities[entityType];
    const fieldOptions = FIELD_OPTIONS_MAP[entityType];
    const cfg = ENTITY_CONFIG[entityType];
    const label = entityType === 'jobs' ? jobLabel : cfg.label;
    const backStepMap = {
      customers: STEP.CUST_UPLOAD, jobs: STEP.JOB_UPLOAD,
      estimates: STEP.EST_UPLOAD, invoices: STEP.INV_UPLOAD,
    };

    return (
      <div className="space-y-4" data-testid={`step-map-${entityType}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Map {label} Fields</h2>
            <p className="text-sm text-muted-foreground">Review and adjust how CSV columns map to system fields.</p>
          </div>
          <Badge variant="outline">{ent.headers.length} columns found</Badge>
        </div>

        <div className="border rounded-lg overflow-hidden max-h-[35vh] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/2">CSV Column</TableHead>
                <TableHead className="w-1/2">Map To</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ent.headers.map(header => (
                <TableRow key={header}>
                  <TableCell className="font-mono text-sm">{header}</TableCell>
                  <TableCell>
                    <Select
                      value={ent.mapping[header] || ''}
                      onValueChange={v => updateEntity(entityType, {
                        mapping: { ...ent.mapping, [header]: v }
                      })}
                    >
                      <SelectTrigger className="h-8" data-testid={`wiz-map-${header.replace(/\s+/g, '-').toLowerCase()}`}>
                        <SelectValue placeholder="Skip" />
                      </SelectTrigger>
                      <SelectContent>
                        {fieldOptions.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <MappingSummary headers={ent.headers} mapping={ent.mapping} fieldOptions={fieldOptions} />

        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={() => setStep(backStepMap[entityType])} data-testid="btn-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button
            onClick={() => handlePreview(entityType)}
            disabled={loading}
            data-testid="btn-preview"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Preview &amp; Analyze <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Preview step (generic)
  // ---------------------------------------------------------------------------
  const renderPreviewStep = (entityType) => {
    const ent = entities[entityType];
    const cfg = ENTITY_CONFIG[entityType];
    const label = entityType === 'jobs' ? jobLabel : cfg.label;
    const preview = ent.preview;
    const review = ent.review;
    const backStepMap = {
      customers: STEP.CUST_MAP, jobs: STEP.JOB_MAP,
      estimates: STEP.EST_MAP, invoices: STEP.INV_MAP,
    };

    if (!preview) return null;

    const isSampled = preview.preview_row_count < ent.totalRows;
    const fullFile = estimateFullFileCounts(preview, ent.totalRows);
    const stats = [
      { label: 'Total Rows', value: ent.totalRows, color: '' },
      { label: 'New', value: fullFile.newCount, color: 'text-green-600' },
      { label: 'Duplicates', value: fullFile.dupCount, color: 'text-yellow-600' },
      { label: 'Needs Review', value: review.length, color: review.length > 0 ? 'text-orange-600' : '' },
    ];

    return (
      <div className="space-y-5" data-testid={`step-preview-${entityType}`}>
        <div>
          <h2 className="text-xl font-bold mb-1">{label} Analysis</h2>
          <p className="text-sm text-muted-foreground">
            {isSampled
              ? `Previewing first ${preview.preview_row_count} rows of ${ent.totalRows.toLocaleString()} total. Full-file counts below reflect the entire import.`
              : `We've analyzed all ${ent.totalRows.toLocaleString()} rows. Review the results below.`}
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {stats.map(s => (
            <Card key={s.label} className="text-center py-3">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </Card>
          ))}
        </div>
        {isSampled && (
          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 p-2.5 rounded-lg" data-testid="preview-sample-notice">
            <Info className="w-4 h-4 flex-shrink-0 text-blue-500 mt-0.5" />
            <span>Imports are processed automatically in batches for performance. The preview shows the first {preview.preview_row_count} rows, but totals above reflect the full file.</span>
          </div>
        )}

        {/* Review queue */}
        {review.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              {review.length} item{review.length !== 1 ? 's' : ''} need review
            </h3>
            <div className="border rounded-lg max-h-[200px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Row Data</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {review.map(row => (
                    <TableRow key={row.id}>
                      <TableCell className="text-sm">
                        {row.display_name || row.customer_name || row.estimate_number || row.invoice_number || row.job_number || JSON.stringify(row.raw_data || {}).slice(0, 60)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={row.confidence >= 80 ? 'default' : 'secondary'}>
                          {row.confidence}%
                        </Badge>
                      </TableCell>
                      <TableCell className="space-x-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => handleResolve(entityType, row.id, 'create_new', null)}
                          data-testid={`resolve-new-${row.id}`}
                        >Import New</Button>
                        {row.matched_record_id && (
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => handleResolve(entityType, row.id, 'skip', row.matched_record_id)}
                            data-testid={`resolve-skip-${row.id}`}
                          >Skip</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={() => setStep(backStepMap[entityType])} data-testid="btn-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button
            onClick={() => setStep(getNextAfterPreview(entityType))}
            disabled={review.length > 0}
            data-testid="btn-next-after-preview"
          >
            {getNextLabel(entityType)}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Summary
  // ---------------------------------------------------------------------------
  const renderSummaryStep = () => {
    const activeEntities = ['customers', 'jobs', 'estimates', 'invoices'].filter(
      e => entities[e].preview && !entities[e].skipped
    );

    // Back button should go to the last active preview step
    const getBackStep = () => {
      const lastActive = [...activeEntities].reverse()[0];
      const previewSteps = {
        customers: STEP.CUST_PREVIEW, jobs: STEP.JOB_PREVIEW,
        estimates: STEP.EST_PREVIEW, invoices: STEP.INV_PREVIEW,
      };
      return previewSteps[lastActive] || STEP.CUST_PREVIEW;
    };

    return (
      <div className="space-y-6 max-w-xl" data-testid="step-summary">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-1">Migration Summary</h2>
          <p className="text-sm text-muted-foreground">Review your migration before importing.</p>
        </div>

        <div className="space-y-4">
          {activeEntities.map(entityType => {
            const ent = entities[entityType];
            const cfg = ENTITY_CONFIG[entityType];
            const Icon = cfg.icon;
            const label = entityType === 'jobs' ? jobLabel : cfg.label;
            const fullFile = estimateFullFileCounts(ent.preview, ent.totalRows);
            return (
              <Card key={entityType}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${cfg.color}`} /> {label}
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-3 gap-2 text-center">
                  <div><p className="text-xl font-bold">{ent.totalRows.toLocaleString()}</p><p className="text-xs text-muted-foreground">rows found</p></div>
                  <div><p className="text-xl font-bold text-green-600">{fullFile.newCount.toLocaleString()}</p><p className="text-xs text-muted-foreground">new</p></div>
                  <div><p className="text-xl font-bold text-yellow-600">{fullFile.dupCount.toLocaleString()}</p><p className="text-xs text-muted-foreground">duplicates</p></div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Estimated migration time */}
        {(() => {
          let totalSec = 0;
          for (const et of activeEntities) {
            const rows = entities[et].totalRows || 0;
            const batches = Math.ceil(rows / BATCH_SIZE);
            totalSec += batches * (BATCH_TIME_SEC[et] || 30);
          }
          const mins = Math.ceil(totalSec / 60);
          return (
            <div className="bg-muted/30 border rounded-lg p-4 text-center" data-testid="estimated-time">
              <p className="text-sm font-medium">
                Estimated migration time: ~{mins} minute{mins !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Estimate based on typical batch times. Actual time may vary based on file contents and server load.
              </p>
            </div>
          );
        })()}

        <div className="bg-muted/30 border rounded-lg p-4 text-sm space-y-1">
          <p className="font-medium flex items-center gap-2"><Shield className="w-4 h-4" /> Import Order</p>
          <ol className="list-decimal list-inside text-muted-foreground space-y-0.5">
            {activeEntities.map(entityType => {
              const ent = entities[entityType];
              const cfg = ENTITY_CONFIG[entityType];
              const label = entityType === 'jobs' ? jobLabel.toLowerCase() : cfg.label.toLowerCase();
              const fullFile = estimateFullFileCounts(ent.preview, ent.totalRows);
              return <li key={entityType}>Import {label} ({fullFile.newCount.toLocaleString()} new)</li>;
            })}
            {activeEntities.length > 1 && <li>Link records automatically</li>}
          </ol>
          <p className="text-xs text-muted-foreground mt-2">You can undo the entire migration after import.</p>
        </div>

        <div className="flex justify-between">
          <Button variant="ghost" onClick={() => setStep(getBackStep())} data-testid="btn-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button onClick={() => { setStep(STEP.EXECUTE); handleExecute(); }} size="lg" data-testid="btn-execute-migration">
            Start Migration <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Executing
  // ---------------------------------------------------------------------------
  const renderExecuteStep = () => {
    const order = ['customers', 'jobs', 'estimates', 'invoices'];
    const activeEntities = order.filter(e => entities[e].session && !entities[e].skipped);

    return (
      <div className="space-y-6 max-w-lg py-8" data-testid="step-execute">
        <div className="text-center">
          {executeFailed ? (
            <AlertTriangle className="w-12 h-12 mx-auto text-red-500 mb-3" />
          ) : (
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary mb-3" />
          )}
          <h2 className="text-xl font-bold mb-1">
            {executeFailed ? 'Import Error' : 'Migration in Progress'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {executeFailed || executionPhase || 'Preparing...'}
          </p>
        </div>

        <div className="space-y-3">
          {activeEntities.map(entityType => {
            const cfg = ENTITY_CONFIG[entityType];
            const Icon = cfg.icon;
            const label = entityType === 'jobs' ? jobLabel : cfg.label;
            const prog = executeProgress[entityType];
            const pct = prog && prog.progress_total > 0
              ? Math.round((prog.progress_current / prog.progress_total) * 100)
              : 0;
            const isDone = prog?.status === 'completed';
            const isFailed = prog?.status === 'failed';
            const isBlocked = prog?.status === 'blocked';
            const hasErrors = isDone && (prog?.error_count > 0);
            const isRunning = prog && !isDone && !isFailed && !isBlocked && prog.status !== 'queued';

            return (
              <Card key={entityType} className={`transition-all ${isDone && !hasErrors ? 'border-green-200 bg-green-50/50' : isDone && hasErrors ? 'border-amber-200 bg-amber-50/50' : isFailed ? 'border-red-200 bg-red-50/50' : isBlocked ? 'border-gray-200 bg-gray-50/50' : ''}`}>
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isDone && !hasErrors ? 'text-green-600' : isDone && hasErrors ? 'text-amber-600' : isFailed ? 'text-red-500' : isBlocked ? 'text-gray-400' : cfg.color}`} />
                    <span className="text-sm font-medium flex-1">{label}</span>
                    {isDone && !hasErrors && <CheckCircle2 className="w-4 h-4 text-green-600" data-testid={`${entityType}-execute-done`} />}
                    {isDone && hasErrors && <AlertTriangle className="w-4 h-4 text-amber-500" data-testid={`${entityType}-execute-partial`} />}
                    {isFailed && <AlertTriangle className="w-4 h-4 text-red-500" data-testid={`${entityType}-execute-failed`} />}
                    {isBlocked && <Shield className="w-4 h-4 text-gray-400" data-testid={`${entityType}-execute-blocked`} />}
                    {isRunning && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
                    {!prog && <span className="text-xs text-muted-foreground">Waiting</span>}
                  </div>
                  {prog && !isBlocked && (
                    <>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-1.5">
                        <div
                          className={`h-2 rounded-full transition-all duration-500 ${isDone && !hasErrors ? 'bg-green-500' : isDone && hasErrors ? 'bg-amber-500' : isFailed ? 'bg-red-400' : 'bg-primary'}`}
                          style={{ width: `${isDone ? 100 : pct}%` }}
                          data-testid={`${entityType}-progress-bar`}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground" data-testid={`${entityType}-progress-msg`}>
                        {isDone
                          ? (prog.progress_message || (hasErrors
                            ? `Done — ${prog.imported_count || 0} imported, ${prog.error_count} row(s) had errors`
                            : `Done — ${prog.imported_count || 0} imported`))
                          : isFailed
                          ? (prog.error_message || 'Failed')
                          : (() => {
                              const current = prog.progress_current || 0;
                              const total = prog.progress_total || 0;
                              const batchNum = total > 0 ? Math.ceil(current / BATCH_SIZE) : 0;
                              const totalBatches = total > 0 ? Math.ceil(total / BATCH_SIZE) : 0;
                              const remainBatches = Math.max(0, totalBatches - batchNum);
                              const secPerBatch = BATCH_TIME_SEC[entityType] || 30;
                              const remainSec = remainBatches * secPerBatch;
                              const remainMin = Math.ceil(remainSec / 60);
                              if (total > 0 && current > 0) {
                                return `Batch ${batchNum} of ${totalBatches} · Processed: ${current.toLocaleString()} / ${total.toLocaleString()} · ~${remainMin} min remaining`;
                              }
                              return prog.progress_message || `${pct}%`;
                            })()}
                      </p>
                    </>
                  )}
                  {isBlocked && (
                    <p className="text-xs text-muted-foreground" data-testid={`${entityType}-blocked-msg`}>
                      {prog.progress_message || 'Blocked — previous import failed'}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {executeFailed && (
          <div className="text-center space-y-3 pt-2">
            <p className="text-xs text-muted-foreground">You can retry the import or go back to review your data.</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => { setExecuteFailed(null); setStep(STEP.SUMMARY); }} data-testid="btn-back-to-summary">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Summary
              </Button>
              <Button onClick={() => { setExecuteFailed(null); handleExecute(); }} data-testid="btn-retry-execute">
                <RotateCcw className="w-4 h-4 mr-1" /> Retry
              </Button>
            </div>
          </div>
        )}

        {!executeFailed && (
          <p className="text-xs text-muted-foreground text-center">
            Your import is running in the background. You can safely stay on this page.
          </p>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Complete
  // ---------------------------------------------------------------------------
  const renderCompleteStep = () => {
    const entityTypes = ['customers', 'jobs', 'estimates', 'invoices'];
    const activeEntities = entityTypes.filter(e => entities[e].summary);
    let totalImported = 0;
    let totalErrors = 0;

    // Collect error details from executeProgress
    const allErrorDetails = {};
    for (const et of entityTypes) {
      const prog = executeProgress[et];
      if (prog?.error_details?.length > 0) {
        allErrorDetails[et] = prog.error_details;
      }
      const sum = entities[et]?.summary;
      if (sum?.errors > 0) totalErrors += sum.errors;
    }
    const hasAnyErrors = totalErrors > 0;

    return (
      <div className="space-y-6 max-w-2xl" data-testid="step-complete">
        <div className="text-center pt-2">
          {hasAnyErrors ? (
            <AlertTriangle className="w-16 h-16 mx-auto text-amber-500 mb-3" />
          ) : (
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-3" />
          )}
          <h2 className="text-2xl font-bold mb-1">
            {hasAnyErrors ? 'Migration Completed with Errors' : 'Migration Complete!'}
          </h2>
          <p className="text-muted-foreground">
            Data imported from {crmOption?.label || 'CSV'}.
            {hasAnyErrors && ` ${totalErrors} row(s) could not be imported.`}
          </p>
        </div>

        <Card data-testid="migration-report">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Migration Report</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeEntities.map(entityType => {
              const sum = entities[entityType].summary;
              const cfg = ENTITY_CONFIG[entityType];
              const Icon = cfg.icon;
              const label = entityType === 'jobs' ? jobLabel : cfg.label;
              const created = sum?.records_created || 0;
              const updated = sum?.records_updated || 0;
              const skipped = (sum?.duplicates_skipped || 0) + (sum?.user_skipped || 0);
              const errors = sum?.errors || 0;
              totalImported += created + updated;

              return (
                <div key={entityType}>
                  <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                    <Icon className={`w-4 h-4 ${cfg.color}`} /> {label}
                  </h4>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600" data-testid={`${entityType}-created`}>{created}</p>
                      <p className="text-xs text-muted-foreground">Created</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-600" data-testid={`${entityType}-updated`}>{updated}</p>
                      <p className="text-xs text-muted-foreground">Updated</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-yellow-600" data-testid={`${entityType}-skipped`}>{skipped}</p>
                      <p className="text-xs text-muted-foreground">Skipped</p>
                    </div>
                    <div className={`rounded-lg p-3 text-center ${errors > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                      <p className={`text-2xl font-bold ${errors > 0 ? 'text-destructive' : 'text-muted-foreground'}`} data-testid={`${entityType}-errors`}>{errors}</p>
                      <p className="text-xs text-muted-foreground">Errors</p>
                    </div>
                  </div>
                  {entityType === 'jobs' && (() => {
                    const prog = executeProgress[entityType];
                    const scheduledEvents = prog?.scheduled_events || 0;
                    const unscheduledJobs = prog?.unscheduled_jobs || 0;
                    if (scheduledEvents > 0 || unscheduledJobs > 0) {
                      return (
                        <div className="grid grid-cols-2 gap-3 mt-2" data-testid="jobs-schedule-breakdown">
                          <div className="bg-emerald-50 rounded-lg p-2.5 text-center">
                            <p className="text-lg font-bold text-emerald-600" data-testid="jobs-scheduled-events">{scheduledEvents}</p>
                            <p className="text-xs text-muted-foreground">Scheduled Events</p>
                          </div>
                          <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                            <p className="text-lg font-bold text-slate-500" data-testid="jobs-unscheduled">{unscheduledJobs}</p>
                            <p className="text-xs text-muted-foreground">Unscheduled</p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Error Details (if any) */}
        {hasAnyErrors && Object.keys(allErrorDetails).length > 0 && (
          <Card className="border-red-200 bg-red-50/30" data-testid="error-details-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" /> Error Details
              </CardTitle>
              <CardDescription>
                Rows that could not be imported due to data issues. Other rows were imported successfully.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-60 overflow-y-auto space-y-3">
                {Object.entries(allErrorDetails).map(([et, details]) => {
                  const cfg = ENTITY_CONFIG[et];
                  return (
                    <div key={et}>
                      <p className="text-xs font-semibold mb-1 text-muted-foreground uppercase">{cfg?.label || et}</p>
                      <div className="space-y-1">
                        {details.map((d, i) => (
                          <div key={i} className="text-xs bg-white rounded p-2 border border-red-100 flex gap-2" data-testid={`error-row-${et}-${i}`}>
                            <span className="font-mono text-red-600 shrink-0">Row {d.row}</span>
                            <span className="text-muted-foreground truncate">{d.error}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unmatched Technician Assignments Notice */}
        {(() => {
          const unmatchedTotal = Object.values(executeProgress).reduce((sum, p) => sum + (p?.unmatched_techs || 0), 0);
          if (unmatchedTotal === 0) return null;
          return (
            <Card className="border-amber-200 bg-amber-50/30" data-testid="unmatched-techs-notice">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">
                    {unmatchedTotal} job{unmatchedTotal !== 1 ? 's' : ''} imported with unmatched technician assignments
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    We preserved the original source technician names in each job's notes so you can review and assign them later.
                  </p>
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate('/dispatch')} data-testid="btn-review-jobs">
                    Review Jobs
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Price Book Import Handoff */}
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-950/10" data-testid="pricebook-handoff">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm mb-0.5">Next: Import Your Price Book</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {(() => {
                    const crmLower = (crm || '').toLowerCase();
                    if (crmLower.includes('housecall')) return 'You can now import your Housecall Pro Price Book so you can quickly build estimates, invoices, and service templates.';
                    if (crmLower.includes('markate')) return 'You can now import your Markate Items catalog to quickly build estimates, invoices, and service templates.';
                    return 'Your core data migration is complete. Import your Price Book next so you can quickly build estimates, invoices, and service templates.';
                  })()}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      const crmLower = (crm || '').toLowerCase();
                      let importParam = '';
                      if (crmLower.includes('housecall')) importParam = 'housecall_pro';
                      else if (crmLower.includes('markate')) importParam = 'markate';
                      navigate(importParam ? `/price-book?import=${importParam}` : '/price-book?import=1');
                    }}
                    data-testid="btn-import-pricebook"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> Import Price Book
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="btn-maybe-later">
                    Maybe Later
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap justify-center gap-2">
          <Button variant="outline" onClick={() => navigate('/customers')} data-testid="btn-view-customers">
            <Users className="w-4 h-4 mr-1" /> View Customers
          </Button>
          <Button variant="outline" onClick={() => navigate('/jobs')} data-testid="btn-view-jobs">
            <Briefcase className="w-4 h-4 mr-1" /> View {jobLabel}
          </Button>
          <Button variant="outline" onClick={() => navigate('/import-history?tab=crm')} data-testid="btn-view-history">
            <ExternalLink className="w-4 h-4 mr-1" /> Import History
          </Button>
          <Button variant="destructive" size="sm" onClick={handleRollback} disabled={loading} data-testid="btn-undo-migration">
            <RotateCcw className="w-4 h-4 mr-1" /> Undo Import
          </Button>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------
  const stepRenderers = {
    [STEP.CRM]: renderStepCRM,
    [STEP.CUST_UPLOAD]: () => renderUploadStep('customers'),
    [STEP.CUST_MAP]: () => renderMappingStep('customers'),
    [STEP.CUST_PREVIEW]: () => renderPreviewStep('customers'),
    [STEP.JOB_UPLOAD]: () => renderUploadStep('jobs'),
    [STEP.JOB_MAP]: () => renderMappingStep('jobs'),
    [STEP.JOB_PREVIEW]: () => renderPreviewStep('jobs'),
    [STEP.EST_UPLOAD]: () => renderUploadStep('estimates'),
    [STEP.EST_MAP]: () => renderMappingStep('estimates'),
    [STEP.EST_PREVIEW]: () => renderPreviewStep('estimates'),
    [STEP.INV_UPLOAD]: () => renderUploadStep('invoices'),
    [STEP.INV_MAP]: () => renderMappingStep('invoices'),
    [STEP.INV_PREVIEW]: () => renderPreviewStep('invoices'),
    [STEP.SUMMARY]: renderSummaryStep,
    [STEP.EXECUTE]: renderExecuteStep,
    [STEP.COMPLETE]: renderCompleteStep,
  };

  return (
    <Layout>
      <div className="px-4 py-6" data-testid="migration-wizard">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Migration Wizard</h1>
            <p className="text-sm text-muted-foreground">Import your data from another CRM system</p>
          </div>
          {step > 0 && step < STEP.EXECUTE && (
            <Button variant="ghost" size="sm" onClick={() => { if (window.confirm('Discard migration progress?')) { setStep(0); setCrm(''); } }} data-testid="btn-start-over">
              Start Over
            </Button>
          )}
        </div>

        {step > 0 && step < STEP.COMPLETE && (
          <PhaseIndicator step={step} subtitle={visibleSteps[step]?.title || ''} />
        )}

        {stepRenderers[step]?.()}
      </div>
    </Layout>
  );
}
